import { Link } from "@tanstack/react-router";
import { BadgeCheck, FileSearch, MailWarning, ShieldCheck } from "lucide-react";

import { getAuthor } from "@/lib/authors";
import { SITE } from "@/lib/site";

/**
 * Kleine EEAT-blokken met een vaste volgorde.
 *
 * Regels die hier bewust in de component zijn vastgelegd, zodat pagina's ze
 * niet zelf kunnen herschikken:
 *   1. Nooit boven de casinolijst. De lijst blijft het eerste element na de intro.
 *   2. `HowWeTested` staat direct ónder de lijst en de bijbehorende afbeelding.
 *   3. `EeatTrustBlocks` staat onderaan de pagina en rendert altijd in deze
 *      volgorde: bronnen & controle, transparantie, correctiebeleid.
 */

/** Blok 4: hoe deze pagina getest is. Staat altijd ónder de casinolijst. */
export function HowWeTested({
  subject,
  providers,
  period,
  extra,
}: {
  /** Waarover deze test gaat, bv. "iDEAL" of "lage stortingen". */
  subject: string;
  /** Aantal geteste aanbieders. */
  providers: number;
  /** Meet- of controleperiode, bijvoorbeeld "november 2026". */
  period: string;
  /** Optionele extra regel met een pagina-specifiek detail. */
  extra?: string;
}) {
  return (
    <section
      aria-labelledby="hoe-wij-testten"
      className="rounded-xl border border-border bg-secondary/40 p-5"
    >
      <div className="flex items-center gap-2 text-primary">
        <FileSearch className="h-4 w-4" aria-hidden />
        <span className="text-xs font-semibold uppercase tracking-wide">Hoe wij dit testten</span>
      </div>
      <h2 id="hoe-wij-testten" className="sr-only">
        Hoe wij {subject} hebben getest
      </h2>
      <ul className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-3">
        <li>
          <strong className="text-foreground">{providers} aanbieders</strong> doorlopen in de kassa,
          telkens hetzelfde script voor {subject}.
        </li>
        <li>
          <strong className="text-foreground">Eigen geld</strong>: elke storting en opname is echt
          uitgevoerd, geen opgave van de aanbieder.
        </li>
        <li>
          <strong className="text-foreground">Meetperiode {period}</strong>, met een hercontrole bij
          elke update van deze pagina.
        </li>
      </ul>
      {extra ? <p className="mt-3 text-sm text-muted-foreground">{extra}</p> : null}
    </section>
  );
}

/** Blokken 6, 7 en 8 in vaste volgorde. Hoort onderaan de pagina. */
export function EeatTrustBlocks({
  authorSlug,
  reviewerSlug,
  updatedAt,
}: {
  authorSlug: string;
  reviewerSlug?: string;
  updatedAt: string;
}) {
  const author = getAuthor(authorSlug);
  const reviewer = reviewerSlug ? getAuthor(reviewerSlug) : null;

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {/* 6. Bronnen & controle */}
      <section
        aria-labelledby="bronnen-controle"
        className="rounded-xl border border-border bg-card p-5"
      >
        <div className="flex items-center gap-2 text-success">
          <BadgeCheck className="h-4 w-4" aria-hidden />
          <span className="text-xs font-semibold uppercase tracking-wide">Bronnen &amp; controle</span>
        </div>
        <h2 id="bronnen-controle" className="mt-2 text-sm font-semibold text-foreground">
          Wie deze pagina schreef en controleerde
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {author ? `Geschreven door ${author.name}, ${author.role.toLowerCase()}.` : null}{" "}
          {reviewer ? `Feitelijk gecontroleerd door ${reviewer.name}.` : null} Laatste controle:{" "}
          {updatedAt}. Alle cijfers komen uit onze eigen metingen en uit de voorwaarden van de
          aanbieder zelf.
        </p>
        <p className="mt-2 text-sm">
          <Link to="/methodologie/" className="font-medium text-primary underline underline-offset-4">
            Zo beoordelen wij casino&apos;s
          </Link>
        </p>
      </section>

      {/* 7. Transparantie */}
      <section
        aria-labelledby="transparantie"
        className="rounded-xl border border-border bg-card p-5"
      >
        <div className="flex items-center gap-2 text-primary">
          <ShieldCheck className="h-4 w-4" aria-hidden />
          <span className="text-xs font-semibold uppercase tracking-wide">Transparantie</span>
        </div>
        <h2 id="transparantie" className="mt-2 text-sm font-semibold text-foreground">
          Onafhankelijk, ook als we een vergoeding krijgen
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Sommige links naar aanbieders zijn advertentielinks. Een positie in onze ranking is niet te
          koop: de volgorde volgt onze meetgegevens en is voor elke pagina identiek. {SITE.name}{" "}
          richt zich uitsluitend op spelers van 18 jaar en ouder.
        </p>
        <p className="mt-2 text-sm">
          <Link to="/verantwoord-spelen/" className="font-medium text-primary underline underline-offset-4">
            Verantwoord spelen
          </Link>
        </p>
      </section>

      {/* 8. Correctiebeleid */}
      <section
        aria-labelledby="correctiebeleid"
        className="rounded-xl border border-border bg-card p-5"
      >
        <div className="flex items-center gap-2 text-primary">
          <MailWarning className="h-4 w-4" aria-hidden />
          <span className="text-xs font-semibold uppercase tracking-wide">Correctiebeleid</span>
        </div>
        <h2 id="correctiebeleid" className="mt-2 text-sm font-semibold text-foreground">
          Klopt er iets niet?
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Zie je een verouderde limiet, kostenpost of doorlooptijd? Meld het en we hermeten het punt.
          Een aangepaste pagina krijgt altijd een nieuwe controledatum, zodat je ziet wanneer we het
          laatst hebben gekeken.
        </p>
        <p className="mt-2 text-sm">
          <Link to="/contact/" className="font-medium text-primary underline underline-offset-4">
            Correctie doorgeven
          </Link>
        </p>
      </section>
    </div>
  );
}
